import { Button, IconButton } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { SheetTitle } from "@/components/ui/sheet";
import AppInputNew from "@/components/Core/AppInput/AppInputNew";
import FilterSheetFrame from "./FilterSheetFrame";
import SavedFiltersManager from "./SavedFiltersManager";
import { SavedFilter } from "./types";

const FloppyDiskIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M2,.5h9.5l4,4V14A1.5,1.5,0,0,1,14,15.5H2A1.5,1.5,0,0,1,.5,14V2A1.5,1.5,0,0,1,2,.5Z" />
		<polyline points="9.5 2.5 9.5 5.5 3.5 5.5 3.5 2.5" />
		<polyline points="12.5 13.5 12.5 9.5 3.5 9.5 3.5 13.5" />
	</svg>
);

interface Props<TDef> {
	isOpen: boolean;
	onClose: () => void;
	definition: TDef;
	onChange: (def: TDef) => void;
	savedFilters: SavedFilter<TDef>[];
	/** org-defined filters, shown read-only in the manage view for context */
	orgFilters?: SavedFilter<TDef>[];
	/** id of the saved filter currently being edited (the "base"), if any */
	activeSavedId?: string;
	onSaveNew: (name: string, def: TDef) => void;
	onUpdateSaved: (id: string, def: TDef) => void;
	onRenameSaved: (id: string, name: string) => void;
	onDeleteSaved: (id: string) => void;
	onReorderSaved: (ordered: SavedFilter<TDef>[]) => void;
	/** Whether a definition has no active constraints (the "All" baseline). */
	isEmptyDefinition: (def: TDef) => boolean;
	/** Whether two definitions are equivalent (used to match the active pill). */
	definitionsEqual: (a: TDef, b: TDef) => boolean;
	/** The empty definition, applied when the user clears the filter. */
	emptyDefinition: TDef;
	/** Renders the domain's filter controls bound to the live definition. */
	renderBuilder: (definition: TDef, onChange: (def: TDef) => void) => React.ReactNode;
}

/**
 * The filter drawer for a virtualized list page. Switches between an "edit" view
 * (the domain's builder + save/clear) and a "manage" view (personal saved
 * filters plus read-only org filters). Saving is driven by in-app dialogs.
 *
 * Generic over the definition type and parameterized by the domain's builder and
 * its empty/equality helpers, so consultants and customers share one panel.
 */
function ListFilterPanel<TDef>({
	isOpen,
	onClose,
	definition,
	onChange,
	savedFilters,
	orgFilters = [],
	activeSavedId,
	onSaveNew,
	onUpdateSaved,
	onRenameSaved,
	onDeleteSaved,
	onReorderSaved,
	isEmptyDefinition,
	definitionsEqual,
	emptyDefinition,
	renderBuilder,
}: Props<TDef>) {
	const { t } = useTranslation();
	const [view, setView] = useState<"edit" | "manage">("edit");

	// Always start in the edit view each time the drawer opens.
	useEffect(() => {
		if (isOpen) setView("edit");
	}, [isOpen]);

	const empty = isEmptyDefinition(definition);
	// The saved filter the current selection exactly corresponds to, if any
	// (reload-safe: matches by definition, not just an in-session selection).
	const activeSaved = savedFilters.find((f) => definitionsEqual(f.definition, definition));
	// The saved filter being edited (its "base") even after it's been changed.
	const baseSaved = savedFilters.find((f) => f.id === activeSavedId);
	const canSaveCurrent = !empty && !activeSaved;

	// Title: saved filter name, or "<name> (modified)" when edited away from it.
	const title = activeSaved
		? activeSaved.name
		: baseSaved
		? `${baseSaved.name} (${t("MODIFIED" as any)})`
		: t("FILTER" as any);

	const openManage = () => setView("manage");

	// Saving is driven by in-app modals (no inline name field). A brand-new filter
	// opens the name modal directly; editing an existing saved filter first asks
	// whether to update it or save the tweak as a new filter.
	const [saveMode, setSaveMode] = useState<null | "confirm" | "name">(null);
	const [nameInput, setNameInput] = useState("");
	const nameInputRef = useRef<HTMLInputElement>(null);

	const closeSave = () => {
		setSaveMode(null);
		setNameInput("");
	};

	const handleSave = () => {
		if (baseSaved) setSaveMode("confirm");
		else setSaveMode("name");
	};

	const confirmSaveNew = () => {
		const name = nameInput.trim();
		if (!name) return;
		onSaveNew(name, definition);
		closeSave();
	};

	// Focus the name field when the name modal opens (covers both opening the
	// dialog directly in "name" mode and switching to it from the "confirm" step).
	useEffect(() => {
		if (saveMode === "name") setTimeout(() => nameInputRef.current?.focus(), 0);
	}, [saveMode]);

	return (
		<>
			<FilterSheetFrame
				open={isOpen}
				onClose={onClose}
				header={
					view === "edit" ? (
						<div className="flex items-center gap-2">
							<SheetTitle>{title}</SheetTitle>
							<button
								type="button"
								title={t("MANAGE_FILTERS" as any)}
								onClick={openManage}
								className="flex h-8 w-8 items-center justify-center rounded-sm border border-solid border-gray-300 opacity-60 hover:border-gray-400 hover:opacity-100"
							>
								<img src="/svg/preference.svg" alt="" className="h-3 w-3" />
							</button>
						</div>
					) : (
						<button
							className="flex items-center gap-2 text-gray-700"
							onClick={() => setView("edit")}
						>
							<span className="text-gray-400">←</span>
							<SheetTitle asChild>
								<span>{t("MANAGE_FILTERS" as any)}</span>
							</SheetTitle>
						</button>
					)
				}
				footer={
					view === "edit" ? (
						<div className="flex items-center justify-between gap-2">
							<Button
								variant="ghost"
								size="sm"
								isDisabled={empty}
								onClick={() => onChange(emptyDefinition)}
							>
								{t("CLEAR" as any)}
							</Button>

							<div className="flex gap-2">
								<IconButton
									size="sm"
									variant="outline"
									aria-label={baseSaved ? t("UPDATE_FILTER" as any) : t("SAVE_FILTER" as any)}
									title={baseSaved ? t("UPDATE_FILTER" as any) : t("SAVE_FILTER" as any)}
									icon={<FloppyDiskIcon />}
									isDisabled={!canSaveCurrent}
									onClick={handleSave}
								/>
								<Button
									size="sm"
									bg="#333333"
									color="white"
									_hover={{ bg: "#444444" }}
									onClick={onClose}
								>
									{t("SHOW_RESULTS" as any)}
								</Button>
							</div>
						</div>
					) : undefined
				}
			>
				{view === "manage" ? (
					<div className="flex flex-col gap-6 px-5 py-5">
						<div>
							<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
								{t("MY_FILTERS" as any)}
							</p>
							<SavedFiltersManager
								savedFilters={savedFilters}
								onRename={onRenameSaved}
								onDelete={onDeleteSaved}
								onReorder={onReorderSaved}
							/>
						</div>

						{orgFilters.length > 0 && (
							<div>
								<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
									{t("ORG_FILTERS" as any)}
								</p>
								<p className="mb-2 text-xs text-gray-400">{t("ORG_FILTERS_MANAGE_HINT" as any)}</p>
								<SavedFiltersManager savedFilters={orgFilters} muted />
							</div>
						)}
					</div>
				) : (
					renderBuilder(definition, onChange)
				)}
			</FilterSheetFrame>

			{/* A single Radix dialog backs both save steps. Because it's a Radix
			    overlay (like the Sheet), the two compose natively — no trapFocus or
			    z-index hacks needed. Driving both steps from one `open` state keeps
			    the "save as new" transition from accidentally dismissing the dialog. */}
			<Dialog
				open={saveMode !== null}
				onOpenChange={(open) => {
					if (!open) closeSave();
				}}
			>
				<DialogContent
					className="block max-w-[26rem]"
					// The "name" step has no description; silence Radix's missing-description
					// warning there without breaking the "confirm" step's real association.
					{...(saveMode === "name" ? { "aria-describedby": undefined } : {})}
				>
					{saveMode === "confirm" ? (
						<>
							<DialogTitle className="mb-1 text-base font-medium">
								{t("SAVE_FILTER" as any)}
							</DialogTitle>
							<DialogDescription className="mb-5 text-sm text-gray-600">
								{t("UPDATE_OR_CREATE_FILTER" as any, { name: baseSaved?.name })}
							</DialogDescription>
							<div className="flex justify-end gap-2">
								<Button size="sm" variant="ghost" onClick={closeSave}>
									{t("CANCEL" as any)}
								</Button>
								<Button size="sm" variant="outline" onClick={() => setSaveMode("name")}>
									{t("SAVE_AS_NEW" as any)}
								</Button>
								<Button
									size="sm"
									bg="#333333"
									color="white"
									_hover={{ bg: "#444444" }}
									onClick={() => {
										if (baseSaved) onUpdateSaved(baseSaved.id, definition);
										closeSave();
									}}
								>
									{t("UPDATE" as any)}
								</Button>
							</div>
						</>
					) : (
						<>
							<DialogTitle className="mb-3 text-base font-medium">
								{t("SAVE_FILTER" as any)}
							</DialogTitle>
							<AppInputNew
								ref={nameInputRef}
								placeholder="FILTER_NAME"
								value={nameInput}
								onChangeText={setNameInput}
								onEnter={confirmSaveNew}
							/>
							<div className="mt-5 flex justify-end gap-2">
								<Button size="sm" variant="ghost" onClick={closeSave}>
									{t("CANCEL" as any)}
								</Button>
								<Button
									size="sm"
									bg="#333333"
									color="white"
									_hover={{ bg: "#444444" }}
									isDisabled={!nameInput.trim()}
									onClick={confirmSaveNew}
								>
									{t("SAVE" as any)}
								</Button>
							</div>
						</>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
}

export default ListFilterPanel;
